import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { canWrite } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AUTOMATED_TYPES = ['Client Payment', 'Supplier Payment', 'Withdrawal', 'Outsourcing', 'Salaries'];

export const GeneralLedger = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [entryMode, setEntryMode] = useState('expense');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [newCat, setNewCat] = useState({ category: '', subcategory: '', item: '', item_ar: '', is_capital: false });

  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    transaction_type: '',
    description: '',
    amount: 0,
    expense_category_id: null
  });

  const categories = useMemo(() => {
    const seen = new Set();
    return expenseCategories
      .filter(c => { if (seen.has(c.category)) return false; seen.add(c.category); return true; })
      .sort((a, b) => a.category.localeCompare(b.category));
  }, [expenseCategories]);

  const subcategories = useMemo(() => {
    if (!selectedCategory) return [];
    const seen = new Set();
    return expenseCategories
      .filter(c => c.category === selectedCategory && c.subcategory && !seen.has(c.subcategory) && seen.add(c.subcategory))
      .sort((a, b) => a.subcategory.localeCompare(b.subcategory));
  }, [expenseCategories, selectedCategory]);

  const items = useMemo(() => {
    if (!selectedCategory) return [];
    return expenseCategories.filter(c => {
      if (c.category !== selectedCategory) return false;
      if (subcategories.length > 0) return c.subcategory === selectedSubcategory;
      return true;
    });
  }, [expenseCategories, selectedCategory, selectedSubcategory, subcategories]);

  const getCategoryLabel = (entry) => {
    if (!entry.expense_category_id) return entry.transaction_type;
    const cat = expenseCategories.find(c => c.id === entry.expense_category_id);
    if (!cat) return entry.transaction_type;
    const isAr = language === 'ar';
    const parts = [isAr && cat.category_ar ? cat.category_ar : cat.category];
    if (cat.subcategory) parts.push(isAr && cat.subcategory_ar ? cat.subcategory_ar : cat.subcategory);
    parts.push(isAr && cat.item_ar ? cat.item_ar : cat.item);
    return parts.join(' › ');
  };

  const fetchEntries = async () => {
    try {
      const response = await axios.get(`${API}/ledger`, { headers: getAuthHeader() });
      setEntries(Array.isArray(response.data) ? response.data : []);
    } catch {
      toast.error('Failed to fetch ledger entries');
    }
  };

  const fetchExpenseCategories = async () => {
    try {
      const response = await axios.get(`${API}/expense-categories`, { headers: getAuthHeader() });
      setExpenseCategories(response.data || []);
    } catch {
      toast.error('Failed to fetch expense categories');
    }
  };

  useEffect(() => {
    fetchEntries();
    fetchExpenseCategories();
  }, []);

  const handleCategoryChange = (cat) => {
    setSelectedCategory(cat);
    setSelectedSubcategory('');
    setFormData(prev => ({ ...prev, transaction_type: '', expense_category_id: null }));
  };

  const handleSubcategoryChange = (sub) => {
    setSelectedSubcategory(sub);
    setFormData(prev => ({ ...prev, transaction_type: '', expense_category_id: null }));
  };

  const handleItemChange = (id) => {
    const cat = expenseCategories.find(c => c.id === id);
    setFormData(prev => ({
      ...prev,
      transaction_type: cat ? cat.item : '',
      expense_category_id: id || null
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amount = parseFloat(formData.amount) || 0;
    const payload = entryMode === 'finance'
      ? { date: formData.date, transaction_type: 'Finance', description: formData.description, debit: amount, credit: 0, balance: 0, expense_category_id: null }
      : { date: formData.date, transaction_type: formData.transaction_type, description: formData.description, debit: 0, credit: amount, balance: 0, expense_category_id: formData.expense_category_id };
    if (entryMode === 'expense' && !payload.expense_category_id) {
      toast.error('Please select an expense category');
      return;
    }
    try {
      await axios.post(`${API}/ledger`, payload, { headers: getAuthHeader() });
      toast.success('Entry added successfully');
      setIsOpen(false);
      fetchEntries();
      setSelectedCategory('');
      setSelectedSubcategory('');
      setEntryMode('expense');
      setFormData({
        date: new Date().toISOString().split('T')[0],
        transaction_type: '',
        description: '',
        amount: 0,
        expense_category_id: null
      });
    } catch {
      toast.error('Failed to add entry');
    }
  };

  const handleAddCategory = async () => {
    if (!newCat.category.trim() || !newCat.item.trim()) return;
    try {
      await axios.post(`${API}/expense-categories`, newCat, { headers: getAuthHeader() });
      toast.success('Category item added');
      setNewCat({ category: '', subcategory: '', item: '', item_ar: '', is_capital: false });
      setAddingCategory(false);
      fetchExpenseCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDelete = async (date, description) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this ledger entry?')) return;
    try {
      await axios.delete(`${API}/ledger/${date}/${description}`, { headers: getAuthHeader() });
      toast.success('Entry deleted');
      fetchEntries();
    } catch {
      toast.error('Failed to delete entry');
    }
  };

  const filteredEntries = entries.filter(
    e => (!fromDate || e.date >= fromDate) && (!toDate || e.date <= toDate)
  );

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('cashBook')}</h2>
        {canWrite(user?.role) && (
          <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <div className="flex gap-2">
              <Button
                onClick={() => { setEntryMode('finance'); setIsOpen(true); }}
                className="bg-green-600 hover:bg-green-700 text-white font-bold text-xl w-10 h-10 p-0"
                data-testid="add-ledger-btn"
              >+</Button>
              <Button
                onClick={() => { setEntryMode('expense'); setIsOpen(true); }}
                className="bg-red-600 hover:bg-red-700 text-white font-bold text-xl w-10 h-10 p-0"
                data-testid="subtract-ledger-btn"
              >−</Button>
            </div>
            <DialogContent>
              <DialogHeader>
                <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>
                  {entryMode === 'finance' ? t('finance') : t('addCashBookEntry')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</Label>
                  <p className="text-sm px-3 py-2 rounded-md border border-input bg-muted text-muted-foreground">{formData.date}</p>
                </div>

                {/* 3-level category drill-down — only for expense entries */}
                {entryMode === 'expense' && (
                  <>
                    <div>
                      <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('expenseCategory')}</Label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => handleCategoryChange(e.target.value)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        required
                      >
                        <option value="">{t('selectCategory')}</option>
                        {categories.map(cat => (
                          <option key={cat.category} value={cat.category}>
                            {language === 'ar' && cat.category_ar ? cat.category_ar : cat.category}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedCategory && subcategories.length > 0 && (
                      <div>
                        <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('expenseSubcategory')}</Label>
                        <select
                          value={selectedSubcategory}
                          onChange={(e) => handleSubcategoryChange(e.target.value)}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          required
                        >
                          <option value="">{t('selectSubcategory')}</option>
                          {subcategories.map(sub => (
                            <option key={sub.subcategory} value={sub.subcategory}>
                              {language === 'ar' && sub.subcategory_ar ? sub.subcategory_ar : sub.subcategory}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {selectedCategory && (subcategories.length === 0 || selectedSubcategory) && (
                      <div>
                        <div className="flex gap-2 items-center">
                          <div className="flex-1">
                            <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('expenseItem')}</Label>
                            <select
                              value={formData.expense_category_id || ''}
                              onChange={(e) => handleItemChange(e.target.value)}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              required
                            >
                              <option value="">{t('selectItem')}</option>
                              {items.map(item => (
                                <option key={item.id} value={item.id}>
                                  {language === 'ar' && item.item_ar ? item.item_ar : item.item}
                                  {item.is_capital ? ' ★' : ''}
                                </option>
                              ))}
                            </select>
                          </div>
                          {user?.role === 'admin' && (
                            <Button type="button" variant="outline" size="sm" className="shrink-0 mt-5" onClick={() => setAddingCategory(!addingCategory)}>
                              <Plus className="w-4 h-4" />
                            </Button>
                          )}
                        </div>

                        {addingCategory && (
                          <div className="mt-2 p-3 border border-input rounded-md space-y-2">
                            <div className="grid grid-cols-2 gap-2">
                              <Input placeholder="Category" value={newCat.category} onChange={e => setNewCat(p => ({ ...p, category: e.target.value }))} />
                              <Input placeholder="Subcategory" value={newCat.subcategory} onChange={e => setNewCat(p => ({ ...p, subcategory: e.target.value }))} />
                              <Input placeholder="Item (English)" value={newCat.item} onChange={e => setNewCat(p => ({ ...p, item: e.target.value }))} />
                              <Input placeholder="البند بالعربي" value={newCat.item_ar} onChange={e => setNewCat(p => ({ ...p, item_ar: e.target.value }))} dir="rtl" />
                            </div>
                            <div className="flex items-center gap-2">
                              <input type="checkbox" id="is_capital" checked={newCat.is_capital} onChange={e => setNewCat(p => ({ ...p, is_capital: e.target.checked }))} />
                              <label htmlFor="is_capital" className="text-sm">{t('capitalPurchase')}</label>
                            </div>
                            <Button type="button" size="sm" onClick={handleAddCategory} className="bg-accent hover:bg-accent/90" disabled={!newCat.category.trim() || !newCat.item.trim()}>
                              {t('save')}
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</Label>
                  <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} required data-testid="ledger-desc-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>{t('amount')}</Label>
                  <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} data-testid="ledger-amount-input" />
                </div>
                <div className="bg-muted/30 p-3 rounded-md text-sm">
                  <p className="text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('balanceAutoCalc')}</p>
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="ledger-submit-btn">
                  <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex gap-4 mb-4 items-end">
        <div>
          <Label className="text-xs text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('from')}</Label>
          <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="w-40" />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground" lang={language === 'ar' ? 'ar' : 'en'}>{t('to')}</Label>
          <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="w-40" />
        </div>
        {(fromDate || toDate) && (
          <Button variant="ghost" size="sm" onClick={() => { setFromDate(''); setToDate(''); }} className="text-muted-foreground">
            <span lang={language === 'ar' ? 'ar' : 'en'}>{t('clear')}</span>
          </Button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead className="bg-muted/50">
            <tr>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('date')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('type')}</th>
              <th className="border border-border p-3 text-start font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('description')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('debit')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('credit')}</th>
              <th className="border border-border p-3 text-end font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('balance')}</th>
              {user?.role === 'admin' && <th className="border border-border p-3 text-center font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {filteredEntries.map((entry, idx) => (
              <tr key={idx} className="hover:bg-muted/30" data-testid={`ledger-row-${idx}`}>
                <td className="border border-border p-3">{formatDate(entry.date)}</td>
                <td className="border border-border p-3 text-xs">
                  {entry.transaction_type === 'Finance' ? (
                    <span lang="ar">{t('finance')}</span>
                  ) : AUTOMATED_TYPES.includes(entry.transaction_type) ? (
                    <span className="text-muted-foreground">{entry.transaction_type}</span>
                  ) : (
                    getCategoryLabel(entry)
                  )}
                </td>
                <td className="border border-border p-3">{entry.description}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-success">{(entry.debit ?? 0).toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric text-destructive">{(entry.credit ?? 0).toFixed(2)}</td>
                <td className="border border-border p-3 text-end font-mono-numeric font-semibold">{(entry.balance ?? 0).toFixed(2)}</td>
                {user?.role === 'admin' && (
                  <td className="border border-border p-3 text-center">
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.date, entry.description)} data-testid={`delete-ledger-${idx}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {entries.length === 0 && (
          <div className="text-center py-12 text-muted-foreground" data-testid="empty-ledger">
            <p lang={language === 'ar' ? 'ar' : 'en'}>{t('noEntriesYet')}</p>
          </div>
        )}
      </div>
    </Card>
  );
};
